import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const {
    highContrast,
    fontSize,
    showShortcutBadges,
    toggleHighContrast,
    changeFontSize,
    toggleShortcutBadges,
    announce
  } = useAccessibility();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [controlsExpanded, setControlsExpanded] = useState(false);
  const toggleButtonRef = useRef(null);

  // This toolbar is a disclosure widget, not a modal (it doesn't trap
  // focus or block the rest of the page), so useModalDialog isn't the
  // right fit here — but per the WAI-ARIA disclosure pattern, Escape
  // should still close it and return focus to the button that opened it.
  useEffect(() => {
    if (!controlsExpanded) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setControlsExpanded(false);
        announce('Accessibility controls collapsed');
        toggleButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [controlsExpanded, announce]);

  const handleSignOut = async () => {
    await signOut();
    announce('Signed out');
    navigate('/login', { replace: true });
  };

  const handleAccessibilityChange = (action, description) => {
    action();
    announce(description);
  };

  return (
    <header className="header" role="banner">
      <div className="container">
        <div className="header-content">
          {/* Logo and Title */}
          <div className="header-brand">
            <div className="header-logo">
              <img
                src="/Sponsors/Communication Matters.jpg"
                alt="Communication Matters Logo"
                className="brand-logo"
              />
            </div>
            <div className="header-text">
              {/* Not an <h1> — every page already has its own <h1> for the
                  actual page heading inside <main>, which is what screen
                  readers and focusFirstHeading() should land on. This is
                  just persistent site branding, shown on every page, so a
                  second <h1> here would give every single page two
                  top-level headings. The surrounding <header role="banner">
                  landmark already conveys that this is the site identity. */}
              <p className="header-title">
                <span className="sr-only">Communication Matters</span>
                <span aria-hidden="true">CM</span> Conference 2026
              </p>
              <p className="header-subtitle">AAC Conference App</p>
            </div>
          </div>

          {/* Accessibility Controls */}
          <div className="header-controls-wrapper">
            {/* Toggle Button */}
            <button
              ref={toggleButtonRef}
              className="accessibility-toggle"
              onClick={() => {
                setControlsExpanded(!controlsExpanded);
                announce(`Accessibility controls ${!controlsExpanded ? 'expanded' : 'collapsed'}`);
              }}
              aria-expanded={controlsExpanded}
              aria-controls="accessibility-controls"
              aria-label={`${controlsExpanded ? 'Hide' : 'Show'} accessibility controls`}
              title={`${controlsExpanded ? 'Hide' : 'Show'} accessibility controls`}
            >
              <span className="toggle-icon" aria-hidden="true">
                {controlsExpanded ? '🔼' : '⚙️'}
              </span>
              <span className="toggle-text">
                {controlsExpanded ? 'Hide' : 'Settings'}
              </span>
            </button>

            {/* Collapsible Controls */}
            <div
               id="accessibility-controls"
               className={`header-controls ${controlsExpanded ? 'expanded' : 'collapsed'}`}
               role="toolbar"
               aria-label="Accessibility controls"
               aria-hidden={!controlsExpanded}
            >
              <div className="control-group">
                <span className="control-label" id="contrast-label">Display:</span>
                <button
                  className={`btn btn-outline ${highContrast ? 'active' : ''}`}
                  onClick={() => handleAccessibilityChange(
                    toggleHighContrast,
                    `High contrast mode ${!highContrast ? 'enabled' : 'disabled'}`
                  )}
                  aria-labelledby="contrast-label"
                  aria-pressed={highContrast}
                  title={`${highContrast ? 'Disable' : 'Enable'} high contrast mode`}
                  tabIndex={controlsExpanded ? 0 : -1}
                >
                  <span aria-hidden="true">🎨</span>
                  {highContrast ? 'Off' : 'High Contrast'}
                </button>
              </div>

              <div className="control-group">
                <span className="control-label" id="font-label">Text Size:</span>
                <div className="font-size-controls" role="group" aria-labelledby="font-label">
                  {['small', 'off', 'large', 'xl'].map((size) => (
                    <button
                      key={size}
                      className={`btn btn-outline ${fontSize === size ? 'active' : ''}`}
                      onClick={() => handleAccessibilityChange(
                        () => changeFontSize(size),
                        `Font size changed to ${size}`
                      )}
                      aria-pressed={fontSize === size}
                      title={`Set font size to ${size}`}
                      tabIndex={controlsExpanded ? 0 : -1}
                    >
                      <span className={`font-demo font-${size}`}>A</span>
                      <span className="sr-only">{size}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="control-group">
                <span className="control-label" id="badges-label">Shortcut Badges:</span>
                <button
                  className={`btn btn-outline ${showShortcutBadges ? 'active' : ''}`}
                  onClick={() => handleAccessibilityChange(
                    toggleShortcutBadges,
                    `Keyboard shortcut badges ${!showShortcutBadges ? 'shown' : 'hidden'}`
                  )}
                  aria-labelledby="badges-label"
                  aria-pressed={showShortcutBadges}
                  title={`${showShortcutBadges ? 'Hide' : 'Show'} the numbered keyboard shortcut badges`}
                  tabIndex={controlsExpanded ? 0 : -1}
                >
                  <span aria-hidden="true">🔢</span>
                  {showShortcutBadges ? 'On' : 'Off'}
                </button>
              </div>

              {user && (
                <div className="control-group">
                  <span className="control-label" id="signout-label">Account:</span>
                  <button
                    className="btn btn-outline"
                    onClick={handleSignOut}
                    aria-labelledby="signout-label"
                    title="Sign out (or press Alt+0 from any page)"
                    tabIndex={controlsExpanded ? 0 : -1}
                  >
                    <span aria-hidden="true">🚪</span>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </header>
  );
};

export default Header;
